import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import axios from 'axios';
import { Dismiss20Regular, Shield20Regular, Phone20Regular, SpinnerIos16Regular } from '@fluentui/react-icons';
import { GetUserData, logoutWithAudit, setAccessToken, setWorkspaceSelectionRequired } from '../../redux/usersSlice';
import { setProjectImg } from '../../redux/projectDataSlice';
import { setDocs } from '../../redux/documentsSlice';
import { API_BASE_URL, SETUP_2FA, GET_QR_CODE, SEND_SMS_CODE } from '../../apiServices/api_routes';
import CountryCodeSelector from '../CommonComponents/CountryCodeSelector';
import { resolveSetup2FAModalMethod } from '../../utils/mfaMethod';
import modalStyles from './css/Setup2FAModal.module.css';

const OTP_LENGTH = 6;

const Setup2FAModal = ({ isOpen, onClose, userEmail, isUpdate = false, currentMfaMethod = null }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [twoFAMethod, setTwoFAMethod] = useState('qr');
    const [smsStep, setSmsStep] = useState('phone');
    const [loading, setLoading] = useState(false);
    const [qrVerificationCode, setQrVerificationCode] = useState(Array(OTP_LENGTH).fill(''));
    const [smsVerificationCode, setSmsVerificationCode] = useState(Array(OTP_LENGTH).fill(''));
    const [smsData, setSmsData] = useState({ code: '', number: '' });
    const [resendSecondsRemaining, setResendSecondsRemaining] = useState(0);
    const [setupLockoutSecondsRemaining, setSetupLockoutSecondsRemaining] = useState(0);
    const [showSetupLockScreen, setShowSetupLockScreen] = useState(false);
    const [qrData, setQrData] = useState('');
    const [qrLoading, setQrLoading] = useState(false);
    const qrInputRefs = useRef([]);
    const smsInputRefs = useRef([]);

    const isResendDisabled = resendSecondsRemaining > 0;
    const isSetupLocked = setupLockoutSecondsRemaining > 0;
    const setupLockoutMinutes = Math.floor(setupLockoutSecondsRemaining / 60);
    const setupLockoutSeconds = setupLockoutSecondsRemaining % 60;
    const setupLockoutCountdown = `${setupLockoutMinutes}:${String(setupLockoutSeconds).padStart(2, '0')}`;
    const modalTitle = isUpdate ? 'Update two-factor authentication' : 'Set up two-factor authentication';
    const primaryActionLabel = isUpdate ? 'Update 2FA' : 'Set up 2FA';

    useEffect(() => {
        if (!isOpen) return;
        const nextMethod = isUpdate ? resolveSetup2FAModalMethod(currentMfaMethod) : 'qr';
        setTwoFAMethod(nextMethod);
        setSmsStep('phone');
        setQrVerificationCode(Array(OTP_LENGTH).fill(''));
        setSmsVerificationCode(Array(OTP_LENGTH).fill(''));
        setResendSecondsRemaining(0);
        setShowSetupLockScreen(false);
    }, [isOpen, isUpdate, currentMfaMethod]);

    useEffect(() => {
        if (resendSecondsRemaining <= 0) return undefined;
        const intervalId = window.setInterval(() => {
            setResendSecondsRemaining(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => window.clearInterval(intervalId);
    }, [resendSecondsRemaining]);

    useEffect(() => {
        if (!isSetupLocked) return undefined;
        const intervalId = window.setInterval(() => {
            setSetupLockoutSecondsRemaining(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => window.clearInterval(intervalId);
    }, [isSetupLocked]);

    const handleSetupLockoutError = error => {
        if (error?.response?.status !== 423) return false;
        const seconds = Number(error?.response?.data?.retry_after_seconds);
        const remaining = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 5 * 60;
        setSetupLockoutSecondsRemaining(remaining);
        setShowSetupLockScreen(true);
        toast.error('Too many invalid verification attempts. Please try again later.');
        return true;
    };

    const fetchQrCode = async () => {
        if (!userEmail) return;
        try {
            setQrLoading(true);
            const response = await axios.post(`${API_BASE_URL}${GET_QR_CODE}`, {
                email: userEmail,
                update: Boolean(isUpdate),
            });
            setQrData(response.data.qr_data);
        } catch (error) {
            console.error('Error fetching QR code:', error);
            toast.error('Failed to fetch QR code');
        } finally {
            setQrLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && twoFAMethod === 'qr' && userEmail) {
            fetchQrCode();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, twoFAMethod, userEmail, isUpdate]);

    const handleOtpChange = (setter, refs, index, event) => {
        const value = event.target.value.replace(/\D/g, '').slice(-1);
        setter(prev => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
        if (value && index < OTP_LENGTH - 1) refs.current[index + 1]?.focus();
    };

    const handleOtpKeyDown = (code, refs, index, event) => {
        if (event.key === 'Backspace' && !code[index] && index > 0) {
            refs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (setter, event) => {
        const pasted = event.clipboardData.getData('Text').replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (!pasted) return;
        event.preventDefault();
        setter(pasted.split('').concat(Array(OTP_LENGTH).fill('')).slice(0, OTP_LENGTH));
    };

    const handleSmsChange = (field, value) => {
        setSmsData(prev => ({ ...prev, [field]: value }));
    };

    const sessionFrom2FAResponse = async response => {
        const access = response?.data?.access;
        const refresh = response?.data?.refresh;
        if (access && refresh) {
            dispatch(setAccessToken(access));
            Cookies.set('refresh', refresh, { secure: true, sameSite: 'strict' });
            await dispatch(GetUserData());
            toast.success(isUpdate ? '2FA updated successfully.' : '2FA setup completed successfully.');
            handleClose();
            return true;
        }
        return false;
    };

    const logoutAndRedirectToLogin = async () => {
        handleClose();
        Cookies.set('refresh', '');
        dispatch(setProjectImg(''));
        dispatch(setDocs([]));
        dispatch(setWorkspaceSelectionRequired(false));
        await dispatch(logoutWithAudit());
        navigate('/login', { replace: true });
    };

    const handleQrActivation = async () => {
        const verificationCode = qrVerificationCode.join('');
        if (verificationCode.length !== OTP_LENGTH) {
            toast.error('Please enter a 6-digit verification code');
            return;
        }
        try {
            setLoading(true);
            const response = await axios.post(`${API_BASE_URL}${SETUP_2FA}`, {
                email: userEmail,
                mfa_method: 'app',
                verification_code: verificationCode,
                update: Boolean(isUpdate),
            });
            if (await sessionFrom2FAResponse(response)) return;
            toast.success(isUpdate ? '2FA updated successfully! Please sign in again.' : '2FA setup completed successfully! Please sign in again.');
            logoutAndRedirectToLogin();
        } catch (error) {
            if (handleSetupLockoutError(error)) return;
            toast.error(error.response?.data?.error || 'Failed to activate 2FA');
        } finally {
            setLoading(false);
        }
    };

    const handleSendSmsCode = async () => {
        if (!smsData.code || !smsData.number) {
            toast.error('Please enter your phone number');
            return;
        }
        try {
            setLoading(true);
            const phoneNumber = smsData.code + smsData.number;
            await axios.post(`${API_BASE_URL}${SEND_SMS_CODE}`, {
                email: userEmail,
                phone_number: phoneNumber,
            });
            setSmsStep('verification');
            setResendSecondsRemaining(30);
            toast.success('Verification code sent to your phone!');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to send verification code. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSmsActivation = async () => {
        const verificationCode = smsVerificationCode.join('');
        if (verificationCode.length !== OTP_LENGTH) {
            toast.error('Please enter a 6-digit verification code');
            return;
        }
        try {
            setLoading(true);
            const phoneNumber = smsData.code + smsData.number;
            const response = await axios.post(`${API_BASE_URL}${SETUP_2FA}`, {
                email: userEmail,
                mfa_method: 'sms_twilio',
                phone_number: phoneNumber,
                verification_code: verificationCode,
                update: Boolean(isUpdate),
            });
            if (await sessionFrom2FAResponse(response)) return;
            toast.success(isUpdate ? '2FA updated successfully! Please sign in again.' : '2FA setup completed successfully! Please sign in again.');
            logoutAndRedirectToLogin();
        } catch (error) {
            if (handleSetupLockoutError(error)) return;
            toast.error(error.response?.data?.error || 'Invalid verification code. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setTwoFAMethod('qr');
        setSmsStep('phone');
        setQrVerificationCode(Array(OTP_LENGTH).fill(''));
        setSmsVerificationCode(Array(OTP_LENGTH).fill(''));
        setSmsData({ code: '', number: '' });
        setResendSecondsRemaining(0);
        setSetupLockoutSecondsRemaining(0);
        setShowSetupLockScreen(false);
        setQrData('');
        onClose();
    };

    const switchMethod = method => {
        setTwoFAMethod(method);
        if (method === 'qr') {
            setSmsStep('phone');
        }
    };

    const renderOtpInputs = (code, setter, refs) => (
        <div className={modalStyles.otpRow}>
            {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                <input
                    key={index}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={code[index]}
                    onChange={e => handleOtpChange(setter, refs, index, e)}
                    onKeyDown={e => handleOtpKeyDown(code, refs, index, e)}
                    onPaste={e => handleOtpPaste(setter, e)}
                    ref={ref => {
                        refs.current[index] = ref;
                    }}
                    className={modalStyles.otpInput}
                    aria-label={`Verification digit ${index + 1}`}
                />
            ))}
        </div>
    );

    const renderMethodTabs = () => (
        <div className={modalStyles.methodTabs} role="tablist" aria-label="Two-factor authentication method">
            <button
                type="button"
                role="tab"
                aria-selected={twoFAMethod === 'qr'}
                className={twoFAMethod === 'qr' ? `${modalStyles.methodTab} ${modalStyles.methodTabActive}` : modalStyles.methodTab}
                onClick={() => switchMethod('qr')}>
                <Shield20Regular className={modalStyles.methodTabIcon} aria-hidden />
                Authenticator app
            </button>
            <button
                type="button"
                role="tab"
                aria-selected={twoFAMethod === 'sms'}
                className={twoFAMethod === 'sms' ? `${modalStyles.methodTab} ${modalStyles.methodTabActive}` : modalStyles.methodTab}
                onClick={() => switchMethod('sms')}>
                <Phone20Regular className={modalStyles.methodTabIcon} aria-hidden />
                SMS
            </button>
        </div>
    );

    const renderQrContent = () => (
        <div className={modalStyles.content}>
            <p className={modalStyles.description}>
                Scan the QR code with your authenticator app, then enter the verification code to confirm the change.
            </p>
            <div className={modalStyles.qrPanel}>
                <div className={qrLoading ? modalStyles.qrPlaceholder : modalStyles.qrImageWrap}>
                    {qrLoading ? (
                        <SpinnerIos16Regular className="rotateLoader" />
                    ) : qrData ? (
                        <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(qrData)}`}
                            alt="Authenticator QR code"
                        />
                    ) : (
                        <span className={modalStyles.qrCaption}>QR code unavailable</span>
                    )}
                </div>
                <p className={modalStyles.qrCaption}>Use Microsoft Authenticator, or similar</p>
            </div>
            <div>
                <p className={modalStyles.fieldLabel}>Verification code</p>
                {renderOtpInputs(qrVerificationCode, setQrVerificationCode, qrInputRefs)}
            </div>
            <div className={modalStyles.actions}>
                <button
                    type="button"
                    className={modalStyles.primaryButton}
                    onClick={handleQrActivation}
                    disabled={loading || qrVerificationCode.join('').length !== OTP_LENGTH}>
                    {primaryActionLabel}
                    {loading ? <SpinnerIos16Regular className="rotateLoader" /> : null}
                </button>
                <button type="button" className={modalStyles.cancelButton} onClick={handleClose}>
                    Cancel
                </button>
            </div>
        </div>
    );

    const renderSmsPhoneContent = () => (
        <div className={modalStyles.content}>
            <p className={modalStyles.description}>
                Enter your mobile number. Lens will send a verification code to confirm the change.
            </p>
            <div className={modalStyles.phoneRow}>
                <div className={`${modalStyles.inputGroup} ${modalStyles.phoneFieldNarrow}`}>
                    <span className={modalStyles.inputLabel}>Country code</span>
                    <CountryCodeSelector
                        value={smsData.code}
                        onChange={value => handleSmsChange('code', value)}
                        placeholder="+44"
                        className={modalStyles.countryCodeSelect}
                        name="code"
                        ariaLabel="Country code"
                        insideModal
                    />
                </div>
                <div className={`${modalStyles.inputGroup} ${modalStyles.phoneFieldWide}`}>
                    <label className={modalStyles.inputLabel} htmlFor="setup-2fa-mobile">
                        Mobile number
                    </label>
                    <input
                        id="setup-2fa-mobile"
                        type="tel"
                        placeholder="07700 900 000"
                        value={smsData.number}
                        onChange={e => handleSmsChange('number', e.target.value)}
                        className={modalStyles.textInput}
                    />
                </div>
            </div>
            <div className={modalStyles.actions}>
                <button
                    type="button"
                    className={modalStyles.primaryButton}
                    onClick={handleSendSmsCode}
                    disabled={!smsData.code || !smsData.number || loading}>
                    Send Code
                    {loading ? <SpinnerIos16Regular className="rotateLoader" /> : null}
                </button>
                <button type="button" className={modalStyles.cancelButton} onClick={handleClose}>
                    Cancel
                </button>
            </div>
        </div>
    );

    const renderSmsVerificationContent = () => (
        <div className={modalStyles.content}>
            <p className={modalStyles.description}>Enter the verification code sent to your mobile number.</p>
            <div>
                <p className={modalStyles.fieldLabel}>Verification code</p>
                {renderOtpInputs(smsVerificationCode, setSmsVerificationCode, smsInputRefs)}
            </div>
            <div className={modalStyles.actions}>
                <button
                    type="button"
                    className={modalStyles.primaryButton}
                    onClick={handleSmsActivation}
                    disabled={smsVerificationCode.join('').length !== OTP_LENGTH || loading}>
                    {primaryActionLabel}
                    {loading ? <SpinnerIos16Regular className="rotateLoader" /> : null}
                </button>
                <button type="button" className={modalStyles.cancelButton} onClick={() => setSmsStep('phone')}>
                    Cancel
                </button>
            </div>
            <div className={modalStyles.resendRow}>
                <button
                    type="button"
                    className={modalStyles.textLink}
                    onClick={handleSendSmsCode}
                    disabled={isResendDisabled || loading}>
                    {isResendDisabled ? `Resend (${resendSecondsRemaining})` : 'Resend code'}
                </button>
            </div>
        </div>
    );

    if (!isOpen) return null;

    return (
        <div className={modalStyles.overlay} role="presentation" onClick={handleClose}>
            <div
                className={modalStyles.modal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="setup-2fa-modal-title"
                onClick={e => e.stopPropagation()}>
                <div className={modalStyles.header}>
                    <h2 id="setup-2fa-modal-title" className={modalStyles.title}>
                        {showSetupLockScreen ? 'Account temporarily locked' : modalTitle}
                    </h2>
                    <button type="button" className={modalStyles.closeButton} onClick={handleClose} aria-label="Close">
                        <Dismiss20Regular />
                    </button>
                </div>

                {showSetupLockScreen ? (
                    <div className={modalStyles.lockBody}>
                        <p className={modalStyles.lockText}>
                            For security reasons, your account has been temporarily locked after multiple unsuccessful verification
                            attempts. Please try again in {setupLockoutCountdown}.
                        </p>
                        <div className={modalStyles.actions}>
                            <button
                                type="button"
                                className={modalStyles.primaryButton}
                                onClick={() => setShowSetupLockScreen(false)}
                                disabled={isSetupLocked}>
                                Continue
                            </button>
                            <button type="button" className={modalStyles.cancelButton} onClick={handleClose}>
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className={modalStyles.body}>
                        {renderMethodTabs()}
                        {twoFAMethod === 'qr' && renderQrContent()}
                        {twoFAMethod === 'sms' && smsStep === 'phone' && renderSmsPhoneContent()}
                        {twoFAMethod === 'sms' && smsStep === 'verification' && renderSmsVerificationContent()}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Setup2FAModal;
